var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import fetch from "isomorphic-fetch";
import { useCallback } from "react";
export var EventType;
(function (EventType) {
    EventType["Click"] = "click";
    EventType["Event"] = "event";
    EventType["Pageview"] = "pageview";
    EventType["View"] = "view";
})(EventType || (EventType = {}));
const defaultOptions = {
    app: "sundaeswap",
    company: "sundaelabs",
    eventType: EventType.Event,
    source: undefined,
    value: "",
};
const PREVIEW_URL = "https://api.counters.preview.sundaeswap.finance";
const PRODUCTION_URL = "https://api.counters.sundaeswap.finance";
/**
 * `useCountersApi` is a custom hook used to interact with the Counters API.
 * It's used to increment either an 'event' or 'pageview' counter based on the provided options.
 *
 * The counters API endpoint depends on the environment.
 * In development, it connects to the 'api.counters.preview.sundaeswap.finance' endpoint.
 * In production, it connects to the 'api.counters.sundaeswap.finance' endpoint.
 *
 * It fetches the API once the component is mounted.
 *
 * @param {Object} args The arguments object.
 * @param {Object} args.options The options for interacting with the API.
 * @param {string = "sundaeswap"} args.options.app The app that triggers the action.
 * @param {string = "sundaelabs"} args.options.company The company the action is tied to.
 * @param {"event"|"pageview" = "pageview"} args.options.eventType The type of action to report to the API.
 * @param {string | undefined} args.options.source The specific source element that triggered the `eventType` - used with EventType.Click or EventType.Event.
 * @param {string | undefined} args.options.url The specific URL the event occured on.
 * @param {string} args.options.value The specific value of the action to increment in the API.
 * @returns {Promise} Action that triggers the tracking call.
 *
 * @example
 *
 * useCountersApi({ options: { app: "sundaeswap", company: "sundaelabs", source: "WalletButton", url: "/example", type: "event", value: "connect_wallet_button_click" } });
 * // this will increment the 'button_click' event in the Counters API.
 *
 * useCountersApi({ options: { app: "website", company: "sundaelabs", type: "pageview", value: "/home" } });
 * // this will increment the '/home' pageview in the Counters API.
 *
 * @returns {void}
 */
const useCountersApi = () => {
    const setFetchUrl = (options) => {
        var _a, _b, _c, _d, _e, _f;
        const company = (_a = options.company) !== null && _a !== void 0 ? _a : "sundaelabs";
        const app = (_b = options.app) !== null && _b !== void 0 ? _b : "sundaeswap";
        const eventType = (_c = options.eventType) !== null && _c !== void 0 ? _c : EventType.Pageview;
        const source = (_d = options.source) !== null && _d !== void 0 ? _d : undefined;
        const url = (_e = options.url) !== null && _e !== void 0 ? _e : undefined;
        const value = (_f = options.value) !== null && _f !== void 0 ? _f : undefined;
        const countersEndpoint = process.env.NODE_ENV === "development" ? PREVIEW_URL : PRODUCTION_URL;
        return `${countersEndpoint}/api/v0/increment/${company}/products/${app}/${url ? `${url}/` : ""}${source ? `${source}/` : ""}${eventType}${value ? `/${value}` : ""}`;
    };
    const handleCountersApi = useCallback((fetchOptions = defaultOptions) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            return yield fetch(setFetchUrl(fetchOptions));
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.warn("Something went wrong while using the counters API", {
                error,
            });
        }
    }), []);
    return handleCountersApi;
};
export default useCountersApi;
